import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, waitlistApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';

const AdminWaitlistPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    contacted: 0,
    approved: 0,
    professions: []
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfession, setFilterProfession] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Edit notes modal
  const [editingEntry, setEditingEntry] = useState(null);
  const [notesText, setNotesText] = useState('');

  const fetchWaitlist = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await waitlistApi.adminGetWaitlist({
        search: searchTerm,
        profession: filterProfession,
        status: filterStatus,
        page: currentPage,
        limit: itemsPerPage
      });

      if (res && res.success) {
        const fetchedItems = res.items || [];
        const total = typeof res.total === 'number' ? res.total : fetchedItems.length;
        const calculatedPages = res.totalPages || Math.ceil(total / itemsPerPage) || 1;

        if (currentPage > calculatedPages && total > 0) {
          setCurrentPage(calculatedPages);
          return;
        }

        setWaitlist(fetchedItems);
        setTotalCount(total);
        setTotalPages(calculatedPages);
        if (res.stats) {
          setStats(res.stats);
        }
      } else {
        setError(res?.message || 'Failed to load waitlist entries.');
      }
    } catch (err) {
      console.error('Failed to fetch waitlist:', err);
      setError(err.message || 'Failed to connect to waitlist service.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterProfession, filterStatus, currentPage, itemsPerPage]);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchWaitlist();
  }, [authSession?.token, navigate, fetchWaitlist]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await waitlistApi.adminUpdateWaitlist(id, { status: newStatus });
      setWaitlist(prev => prev.map(item => item._id === id ? { ...item, status: newStatus } : item));
      // Refresh stats
      fetchWaitlist();
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

  const handleSaveNotes = async () => {
    if (!editingEntry) return;
    try {
      await waitlistApi.adminUpdateWaitlist(editingEntry._id, { notes: notesText });
      setWaitlist(prev => prev.map(item => item._id === editingEntry._id ? { ...item, notes: notesText } : item));
      setEditingEntry(null);
    } catch (err) {
      alert(err.message || 'Failed to save notes');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to remove ${name} from the waitlist?`)) {
      try {
        await waitlistApi.adminDeleteWaitlist(id);
        setWaitlist(prev => prev.filter(item => item._id !== id));
        fetchWaitlist();
      } catch (err) {
        alert(err.message || 'Failed to delete waitlist entry');
      }
    }
  };

  const handleExportCsv = () => {
    const token = authSession?.token || '';
    let baseUrl = '';
    if (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL !== '/api') {
      baseUrl = process.env.REACT_APP_API_BASE_URL;
    } else if (typeof window !== 'undefined') {
      baseUrl = window.location.hostname === 'localhost' && window.location.port === '3000'
        ? 'http://localhost:5000/api'
        : `${window.location.origin}/api`;
    }
    window.open(`${baseUrl}/admin/waitlist/export/csv?token=${encodeURIComponent(token)}`, '_blank');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return { bg: '#dcfce7', text: '#15803d', label: 'Approved' };
      case 'contacted':
        return { bg: '#e0e7ff', text: '#4338ca', label: 'Contacted' };
      case 'rejected':
        return { bg: '#fee2e2', text: '#b91c1c', label: 'Rejected' };
      default:
        return { bg: '#fef3c7', text: '#b45309', label: 'Pending' };
    }
  };

  const getProfessionBadgeColor = (prof = '') => {
    const p = prof.toLowerCase();
    if (p.includes('psycholog')) return { bg: '#fce7f3', text: '#be185d' };
    if (p.includes('counsell') || p.includes('counsel')) return { bg: '#e0f2fe', text: '#0369a1' };
    if (p.includes('therap')) return { bg: '#fef3c7', text: '#b45309' };
    if (p.includes('client')) return { bg: '#d1fae5', text: '#047857' };
    if (p.includes('doctor') || p.includes('psychiat')) return { bg: '#ede9fe', text: '#6d28d9' };
    return { bg: '#f3f4f6', text: '#4b5563' };
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <div style={{ flex: 1, marginLeft: '280px', padding: '30px', maxWidth: 'calc(100% - 280px)' }}>
        
        {/* Header Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
              ⏳ Waitlist Signups
            </h1>
            <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
              Real-time early access registrations captured from the Saathi Waitlist page
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleExportCsv}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                backgroundColor: '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)'
              }}
            >
              📥 Export CSV
            </button>
            <button
              onClick={fetchWaitlist}
              style={{
                padding: '10px 16px',
                backgroundColor: '#fff',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Top Metric Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Total Signups</span>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginTop: '6px' }}>{stats.total || 0}</div>
          </div>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#b45309', textTransform: 'uppercase' }}>Pending Review</span>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#b45309', marginTop: '6px' }}>{stats.pending || 0}</div>
          </div>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#4338ca', textTransform: 'uppercase' }}>Contacted</span>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#4338ca', marginTop: '6px' }}>{stats.contacted || 0}</div>
          </div>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#15803d', textTransform: 'uppercase' }}>Approved</span>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#15803d', marginTop: '6px' }}>{stats.approved || 0}</div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div style={{
          backgroundColor: '#fff',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
          display: 'flex',
          gap: '14px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Search Box */}
          <div style={{ flex: 1, minWidth: '240px' }}>
            <input
              type="text"
              placeholder="Search by name, email, phone, profession..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Profession Filter */}
          <div>
            <select
              value={filterProfession}
              onChange={(e) => { setFilterProfession(e.target.value); setCurrentPage(1); }}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Professions</option>
              <option value="Psychologist">Psychologist</option>
              <option value="Counsellor">Counsellor</option>
              <option value="Psychiatrist">Psychiatrist</option>
              <option value="Therapist">Therapist</option>
              <option value="Social worker">Social worker</option>
              <option value="Student">Student</option>
              <option value="Client">Client</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div style={{
            padding: '14px 18px',
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Data Table */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
              Loading waitlist signups...
            </div>
          ) : waitlist.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
              <h3 style={{ fontSize: '18px', color: '#1e293b', marginBottom: '6px' }}>No waitlist entries found</h3>
              <p style={{ fontSize: '14px' }}>Signups submitted on the waitlist page will appear here instantly.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: '700' }}>
                    <th style={{ padding: '14px 18px', width: '50px' }}>#</th>
                    <th style={{ padding: '14px 18px' }}>User Details</th>
                    <th style={{ padding: '14px 18px' }}>Contact</th>
                    <th style={{ padding: '14px 18px' }}>Profession</th>
                    <th style={{ padding: '14px 18px' }}>Status</th>
                    <th style={{ padding: '14px 18px' }}>Joined Date</th>
                    <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(waitlist.length > itemsPerPage
                    ? waitlist.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    : waitlist
                  ).map((item, idx) => {
                    const statusBadge = getStatusBadge(item.status);
                    const profBadge = getProfessionBadgeColor(item.profession);
                    const phoneClean = (item.phone || '').replace(/\D/g, '');

                    return (
                      <tr
                        key={item._id}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '14px 18px', color: '#94a3b8', fontWeight: '600' }}>
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        
                        {/* User Details */}
                        <td style={{ padding: '14px 18px' }}>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>{item.name}</div>
                          {item.notes && (
                            <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '2px', fontStyle: 'italic' }}>
                              Note: {item.notes}
                            </div>
                          )}
                        </td>

                        {/* Contact */}
                        <td style={{ padding: '14px 18px' }}>
                          <div>
                            <a href={`mailto:${item.email}`} style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>
                              {item.email}
                            </a>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                            <a href={`tel:${item.phone}`} style={{ color: '#475569', textDecoration: 'none', fontSize: '13px' }}>
                              {item.phone}
                            </a>
                            {phoneClean && (
                              <a
                                href={`https://wa.me/${phoneClean}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Chat on WhatsApp"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  fontSize: '11px',
                                  padding: '1px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: '#dcfce7',
                                  color: '#15803d',
                                  textDecoration: 'none',
                                  fontWeight: '600'
                                }}
                              >
                                WA
                              </a>
                            )}
                          </div>
                        </td>

                        {/* Profession */}
                        <td style={{ padding: '14px 18px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: '700',
                            backgroundColor: profBadge.bg,
                            color: profBadge.text
                          }}>
                            {item.profession}
                          </span>
                        </td>

                        {/* Status dropdown */}
                        <td style={{ padding: '14px 18px' }}>
                          <select
                            value={item.status || 'pending'}
                            onChange={(e) => handleStatusChange(item._id, e.target.value)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '700',
                              border: 'none',
                              backgroundColor: statusBadge.bg,
                              color: statusBadge.text,
                              cursor: 'pointer',
                              outline: 'none'
                            }}
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>

                        {/* Joined Date */}
                        <td style={{ padding: '14px 18px', color: '#64748b', fontSize: '13px' }}>
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '—'}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '8px' }}>
                            <button
                              onClick={() => {
                                setEditingEntry(item);
                                setNotesText(item.notes || '');
                              }}
                              title="Add/Edit Note"
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                background: '#fff',
                                color: '#475569',
                                fontSize: '12px',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                            >
                              ✏️ Note
                            </button>
                            <button
                              onClick={() => handleDelete(item._id, item.name)}
                              title="Delete Signup"
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#fee2e2',
                                color: '#b91c1c',
                                fontSize: '12px',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onItemsPerPageChange={(newSize) => {
                setItemsPerPage(newSize);
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        {/* Edit Notes Modal */}
        {editingEntry && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '14px',
              padding: '24px',
              width: '90%',
              maxWidth: '480px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#0f172a' }}>
                Edit Notes for {editingEntry.name}
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                Add follow-up notes, phone call updates, or invite onboarding details.
              </p>
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Enter internal follow-up note..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  marginBottom: '16px'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setEditingEntry(null)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#475569',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveNotes}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#6366f1',
                    color: '#fff',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminWaitlistPage;
